import { s3ApiUploader, s3ApiCopier } from './s3ApiConnectors';
import { log, MAX_FILE_SIZE } from './s3Config';

/**
 * Upload files to S3, using either direct SDK or API based on configuration
 * @param {Array} filesToUpload - Array of files to upload
 * @param {String} bucket - Bucket type identifier
 * @param {String} path - Path within bucket
 * @param {String} token - Optional JWT token (for API implementation)
 * @param {Object} options - Additional options
 * @returns {Array} - Array of S3 file URLs
 */
export const uploadFilesToS3 = async (filesToUpload, bucket, path, token = null, options = {}) => {
  try {
    // Validate inputs
    if (!filesToUpload || !Array.isArray(filesToUpload)) {
      log.error('Invalid filesToUpload parameter', filesToUpload);
      throw new Error('Files to upload must be an array');
    }

    if (!bucket) {
      log.error('Missing bucket parameter');
      throw new Error('Bucket parameter is required');
    }

    if (!path) {
      log.error('Missing path parameter');
      throw new Error('Path parameter is required');
    }

    // Use custom max file size if provided
    const maxFileSize = options.maxFileSize || MAX_FILE_SIZE;

    // File size validation
    const oversizedFiles = filesToUpload.filter(
      file => typeof file === 'object' && file.size > maxFileSize
    );
    
    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map(f => f.name).join(', ');
      const sizeInMB = Math.round(maxFileSize / (1024 * 1024));
      log.error(`Files exceed maximum size limit (${sizeInMB}MB): ${fileNames}`);
      
      // Create error with code for easier handling
      const error = new Error(`Some files exceed the maximum size limit (${sizeInMB}MB): ${fileNames}`);
      error.code = 'FILE_SIZE_EXCEEDED';
      error.files = oversizedFiles;
      throw error;
    }

    log.info(`Uploading ${filesToUpload.length} files to ${bucket}/${path} using API`);
    
    // (OLD s3Connector)
    return await s3ApiUploader(filesToUpload, bucket, path, token);

  } catch (error) {
    log.error('Error uploading files to S3:', error);
    throw error;
  }
};

/**
 * Copy files between S3 directories
 * @param {String} sourceDir - Original directory
 * @param {String} destDir - Destination directory
 * @param {String} bucket - Bucket type identifier
 * @param {Boolean} deleteOnCopy - Whether to delete original files
 * @param {String} token - Optional JWT token (for API implementation)
 * @returns {Boolean} - Operation success
 */
export const copyS3Files = async (sourceDir, destDir, bucket, deleteOnCopy = false, token = null) => {
  try {
    // Validate inputs
    if (!sourceDir) {
      log.error('Missing sourceDir parameter');
      throw new Error('Source directory parameter is required');
    }

    if (!destDir) {
      log.error('Missing destDir parameter');
      throw new Error('Destination directory parameter is required');
    }

    if (!bucket) {
      log.error('Missing bucket parameter');
      throw new Error('Bucket parameter is required');
    }

    log.info(`Copying files from ${sourceDir} to ${destDir} in bucket ${bucket} using API`);
    
    // (OLD s3DataCopier)
    return await s3ApiCopier(sourceDir, destDir, bucket, deleteOnCopy, token);
    
  } catch (error) {
    log.error('Error copying S3 files:', error);
    return false;
  }
}; 